// src/App.js
import React, { useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from "react-router-dom";
import AuthForm from "./components/AuthForm";
import LandingPage from "./pages/LandingPage";
import Login from "./components/Login";
import RegistrationForm from "./components/RegistrationForm";

import Dashboard from "./pages/Finance/Dashboard";
import Transactions from "./pages/Finance/Transactions";
import Reports from "./pages/Finance/Reports";
import ChartOfAccounts from "./pages/Finance/ChartOfAccounts";
import Payments from "./pages/Finance/Payments";
import TuitionCalculator from "./pages/Finance/TuitionCalculator";
import CashFlow from "./pages/Finance/CashFlow";
import StudentFees from "./pages/Finance/StudentFees";
import TuitionLedger from "./pages/Finance/TuitionLedger";

import FinancialReports from "./pages/Finance/FinancialReports";
import LegacyBalances from "./pages/Finance/LegacyBalances";
import Students from "./pages/sis/Students"; // ✅ Placeholder page

import Sidebar from "./components/Sidebar";
import Topbar from "./components/Topbar";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import SchoolHeader from "./components/SchoolHeader";
import Enrollment from "./pages/sis/Enrollment";
import Subjects from "./pages/sis/Subjects";
import StudentCOR from "./pages/sis/StudentCOR";
import SISFinance from "./pages/sis/Finance";
import StudentEnrollmentForm from "./pages/sis/StudentEnrollmentForm";

import SISLayout from "./layouts/SISLayouts";
import ProtectedRoute from "./components/ProtectedRoute";
import SISDashboard from "./pages/sis/SISDashboard";
import PayrollDashboard from "./pages/HR/PayrollDashboard";
import EmployeeList from "./pages/HR/EmployeeList";
import AttendanceDaily from "./pages/HR/AttendanceDaily";
import HRLayout from "./layouts/HRLayout";
import HRReports from "./pages/HR/HRReports";
import PayrollPrint from "./pages/HR/PayrollPrint";
import PayslipPrint from "./pages/HR/PayslipPrint";
import EmployeeDirectory from "./pages/HR/reports/EmployeeDirectory";
import AttendanceLeave from "./pages/HR/reports/AttendanceLeave";
import PayrollSummary from "./pages/HR/reports/PayrollSummary";
import SalaryCompensation from "./pages/HR/reports/SalaryCompensation";
import PerformanceReviews from "./pages/HR/reports/PerformanceReviews";
import RecruitmentOnboarding from "./pages/HR/reports/RecruitmentOnboarding";
import AdminPanel from "./pages/AdminPanel";
import DataMigration from "./pages/Admin/DataMigration";
import DataCleanup from "./pages/Admin/DataCleanup";
import DataNormalize from "./pages/Admin/DataNormalize";
import RecomputeStudentFees from "./pages/Admin/RecomputeStudentFees";
import SectionsManager from "./pages/Admin/SectionsManager";
import ReassignSections from "./pages/Admin/ReassignSections";
import Announcements from "./pages/Admin/Announcements";
import ClaimsManager from "./pages/Admin/ClaimsManager";
import RecalculateTuition from "./pages/Admin/RecalculateTuition";
import TuitionPricingManager from "./pages/Admin/TuitionPricingManager";
import "./index.css";
import "./custom.css";
import UniformsLayout from "./pages/Uniforms/UniformsLayout";
import UniformsInventory from "./pages/Uniforms/Inventory";
import UniformsSales from "./pages/Uniforms/Sales";
import UniformsProducts from "./pages/Uniforms/Products";
import UniformsSalesReport from "./pages/Uniforms/SalesReport";
import UniformsDeliveries from "./pages/Uniforms/Deliveries";
import UniformsOrders from "./pages/Uniforms/Orders";
import BooksLayout from "./pages/Books/BooksLayout";
import BooksInventory from "./pages/Books/Inventory";
import BooksBundles from "./pages/Books/Bundles";
import BooksSales from "./pages/Books/Sales";
import BooksReports from "./pages/Books/Reports";
import BooksDeliveries from "./pages/Books/Deliveries";
import StudentOrderPrint from "./pages/Books/StudentOrderPrint";
import TeacherDashboard from "./pages/teacher/TeacherDashboard";
import TeacherGradebook from "./pages/teacher/Gradebook";
import TeacherLayout from "./layouts/TeacherLayout";
import TeacherAttendance from "./pages/teacher/TeacherAttendance";
import TeacherClasses from "./pages/teacher/Classes";
import TeacherGradebooks from "./pages/teacher/Gradebooks";
import TeacherClassRoster from "./pages/teacher/ClassRoster";
import TeacherCalendar from "./pages/teacher/TeacherCalendar";
import StudentPortalPage from "./pages/sis/StudentPortalPage";
import Manual from "./pages/Manual";
import SchoolYearManager from "./pages/sis/SchoolYearManager";
import PrintEnrollment from "./pages/sis/PrintEnrollment";
import GlobalToast from "./components/GlobalToast";
import PrintList from "./pages/sis/PrintList";
import Attendance from "./pages/sis/Attendance";
import TeacherWorkloads from "./pages/Admin/TeacherWorkloads";

// ✅ Finance / Admin Layout Wrapper
function AppLayout({ user, setUser }) {
  return (
    <div className="finance-layout d-flex" style={{ minHeight: "100vh" }}>
      <Sidebar />
      <div className="flex-grow-1">
        <SchoolHeader />
        <div className="app">
          <header className="finance-header" style={{ padding: "18px 24px", background: "var(--theme-bg)", borderBottom: "1px solid var(--theme-border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <i className="bi bi-cash-stack" style={{ fontSize: 24, color: "var(--theme-primary)" }}></i>
              <h5 style={{ margin: 0 }}>Finance / Admin System</h5>
            </div>
            <div />
          </header>
          {(() => {
            const primary = String(user?.role || "").toLowerCase();
            const extras = Array.isArray(user?.roles) ? user.roles.map(r => String(r).toLowerCase()) : [];
            const roles = [primary, ...extras].filter(Boolean);
            const privileged = roles.some(r => ["admin","finance","superadmin"].includes(r));
            const uniformsBooksOnly = roles.length > 0 && roles.every(r => ["uniforms","books"].includes(r));
            return privileged || !uniformsBooksOnly ? <Navbar /> : null;
          })()}
          <Outlet />
          <Footer />
        </div>
      </div>
    </div>
  );
}

import { UserProvider, useUser } from "./context/UserContext";

function hasAnyRole(user, wanted = []) {
  const target = (wanted || []).map(r => String(r).toLowerCase());
  const single = String(user?.role || "").toLowerCase();
  const multi = Array.isArray(user?.roles) ? user.roles.map(r => String(r).toLowerCase()) : [];
  const roles = [single, ...multi].filter(Boolean);
  return roles.some(r => target.includes(r));
}

function SISIndexRedirect({ user }) {
  const stored = (() => { try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; } })();
  const current = user || stored;
  const encoderOnly = hasAnyRole(current, ["encoder"]) && !hasAnyRole(current, ["admin", "superadmin", "registrar", "finance"]);
  return <Navigate to={encoderOnly ? "students" : "dashboard"} replace />;
}

function AppContent() {
  const { user, setUser } = useUser();
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <GlobalToast />
      <Routes>
          {/* ✅ Landing Page */}
          <Route path="/" element={<LandingPage />} />
          {/* Public route for Student Portal (Google Auth inside) */}
          <Route path="/portal" element={<StudentPortalPage />} />
          {/* Alias: student portal under /sis/portal (no staff login) */}
          <Route path="/sis/portal" element={<StudentPortalPage />} />
          {/* Staff login form */}
          <Route path="/login" element={<AuthForm setUser={setUser} />} />
          {/* Handbook */}
          <Route path="/manual" element={<Manual />} />

          {/* ✅ Admin Panel (Top-level Route) */}
          <Route path="/admin/dashboard" element={<ProtectedRoute allowedRoles={["admin"]} user={user}>
            <AdminPanel user={user} setUser={setUser} />
          </ProtectedRoute>} />
          <Route path="/admin/migrate" element={<ProtectedRoute allowedRoles={["admin","superadmin"]} user={user}>
            <DataMigration />
          </ProtectedRoute>} />
          <Route path="/admin/cleanup" element={<ProtectedRoute allowedRoles={["admin","superadmin"]} user={user}>
            <DataCleanup />
          </ProtectedRoute>} />
          <Route path="/admin/normalize" element={<ProtectedRoute allowedRoles={["admin","superadmin"]} user={user}>
            <DataNormalize />
          </ProtectedRoute>} />
          <Route path="/admin/recompute-fees" element={<ProtectedRoute allowedRoles={["admin","superadmin","finance","registrar"]} user={user}>
            <RecomputeStudentFees />
          </ProtectedRoute>} />
          <Route path="/admin/sections" element={<ProtectedRoute allowedRoles={["admin","superadmin"]} user={user}>
            <SectionsManager />
          </ProtectedRoute>} />
          <Route path="/admin/reassign-sections" element={<ProtectedRoute allowedRoles={["admin","superadmin"]} user={user}>
            <ReassignSections />
          </ProtectedRoute>} />
          <Route path="/admin/announcements" element={<ProtectedRoute allowedRoles={["admin","superadmin","registrar"]} user={user}>
            <Announcements />
          </ProtectedRoute>} />
          <Route path="/admin/claims-manager" element={<ProtectedRoute allowedRoles={["admin","superadmin"]} user={user}>
            <ClaimsManager />
          </ProtectedRoute>} />
          <Route path="/admin/recalculate-tuition" element={<ProtectedRoute allowedRoles={["admin","superadmin"]} user={user}>
            <RecalculateTuition />
          </ProtectedRoute>} />
          <Route path="/admin/tuition-pricing" element={<ProtectedRoute allowedRoles={["admin","superadmin"]} user={user}>
            <TuitionPricingManager />
          </ProtectedRoute>} />
          <Route path="/admin/teacher-workloads" element={<ProtectedRoute allowedRoles={["admin","superadmin","registrar"]} user={user}>
            <TeacherWorkloads />
          </ProtectedRoute>} />

          {/* ✅ Finance System (Existing Layout - nested routes) */}
          <Route
            path="/finance"
            element={
              <ProtectedRoute allowedRoles={["admin", "finance", "superadmin"]} user={user}>
                <AppLayout user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="dashboard" />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="transactions" element={<Transactions />} />
            <Route path="reports" element={<Reports />} />
            <Route path="chart-of-accounts" element={<Navigate to="/finance/settings/chart-of-accounts" replace />} />
            <Route path="payments" element={<Payments />} />
            <Route path="student-fees" element={<StudentFees />} />
            <Route path="financial-reports" element={<FinancialReports />} />
            <Route path="legacy-balances" element={<LegacyBalances />} />
            <Route path="tuition-calculator" element={<TuitionCalculator />} />
            <Route path="cashflow" element={<CashFlow />} />
            <Route path="tuition-ledger" element={<TuitionLedger />} />
            <Route path="settings/chart-of-accounts" element={<ChartOfAccounts />} />
            <Route path="uniforms" element={<UniformsLayout />}>
              <Route index element={<Navigate to="inventory" />} />
              <Route path="inventory" element={<UniformsInventory />} />
              <Route path="sales" element={<UniformsSales />} />
              <Route path="deliveries" element={<UniformsDeliveries />} />
              <Route path="orders" element={<UniformsOrders />} />
              <Route path="products" element={<UniformsProducts />} />
              <Route path="reports" element={<UniformsSalesReport />} />
            </Route>
            <Route path="books" element={<BooksLayout />}>
              <Route index element={<Navigate to="inventory" />} />
              <Route path="inventory" element={<BooksInventory />} />
              <Route path="bundles" element={<BooksBundles />} />
              <Route path="sales" element={<BooksSales />} />
            <Route path="deliveries" element={<BooksDeliveries />} />
              <Route path="reports" element={<BooksReports />} />
            </Route>
          </Route>
          <Route path="/books/print/student-order" element={<StudentOrderPrint />} />
          <Route path="/teacher">
            <Route
              element={
                <ProtectedRoute allowedRoles={["teacher","admin","superadmin"]} user={user}>
                  <TeacherLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="dashboard" />} />
              <Route path="dashboard" element={<TeacherDashboard />} />
              <Route path="attendance" element={<TeacherAttendance />} />
              <Route path="classes" element={<TeacherClasses />} />
              <Route path="gradebooks" element={<TeacherGradebooks />} />
              <Route path="calendar" element={<TeacherCalendar />} />
              <Route path="class-list" element={<TeacherClassRoster />} />
              <Route path="gradebook" element={<TeacherGradebook />} />
            </Route>
          </Route>

          {/* ✅ Books (Consignment) system for uniforms staff */}
          <Route
            path="/books"
            element={
              <ProtectedRoute allowedRoles={["books","uniforms","finance","admin","superadmin"]} user={user}>
                <BooksLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="inventory" />} />
            <Route path="inventory" element={<BooksInventory />} />
            <Route path="bundles" element={<BooksBundles />} />
            <Route path="sales" element={<BooksSales />} />
            <Route path="deliveries" element={<BooksDeliveries />} />
            <Route path="reports" element={<BooksReports />} />
          </Route>
          {/* ✅ HR Payroll Dashboard (Top-level Route) */}
          <Route
            path="/hr"
            element={
              <ProtectedRoute allowedRoles={["admin","superadmin","finance"]} user={user}>
                <HRLayout user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="payroll" />} />
            <Route path="payroll" element={<PayrollDashboard />} />
            <Route path="employees" element={<EmployeeList />} />
            <Route path="attendance-daily" element={<AttendanceDaily />} />
            <Route path="reports" element={<HRReports />} />
            <Route path="reports/employee-directory" element={<EmployeeDirectory />} />
            <Route path="reports/attendance-leave" element={<AttendanceLeave />} />
            <Route path="reports/payroll-summary" element={<PayrollSummary />} />
            <Route path="reports/salary-compensation" element={<SalaryCompensation />} />
            <Route path="reports/performance-reviews" element={<PerformanceReviews />} />
            <Route path="reports/recruitment-onboarding" element={<RecruitmentOnboarding />} />
            {/* Add more HR routes here if needed */}
          </Route>

          {/* ✅ Uniforms System (Standalone for uniforms staff) */}
          <Route
            path="/uniforms"
            element={
              <ProtectedRoute allowedRoles={["uniforms","books","admin","superadmin"]} user={user}>
                <div className="container-fluid">
                  <UniformsLayout />
                </div>
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="inventory" />} />
            <Route path="inventory" element={<UniformsInventory />} />
            <Route path="sales" element={<UniformsSales />} />
            <Route path="deliveries" element={<UniformsDeliveries />} />
            <Route path="orders" element={<UniformsOrders />} />
            <Route path="products" element={<UniformsProducts />} />
            <Route path="reports" element={<UniformsSalesReport />} />
          </Route>

          {/* ✅ Standalone Print Preview (no HRLayout chrome) */}
          <Route
            path="/print/payroll"
            element={
              <ProtectedRoute user={user}>
                <PayrollPrint />
              </ProtectedRoute>
            }
          />
          <Route
            path="/print/payslips"
            element={
              <ProtectedRoute user={user}>
                <PayslipPrint />
              </ProtectedRoute>
            }
          />

          {/* ✅ Student Information System (New Layout) */}
          <Route
            path="/sis"
            element={
              <ProtectedRoute allowedRoles={["student", "parent", "registrar", "encoder", "admin", "finance", "superadmin"]} user={user}>
                <SISLayout user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          >
            <Route index element={<SISIndexRedirect user={user} />} />
            <Route element={<ProtectedRoute allowedRoles={["encoder", "registrar", "admin", "superadmin"]} user={user}><Outlet /></ProtectedRoute>}>
              <Route path="students" element={<Students />} />
              <Route path="students/:id/enrollment-form" element={<StudentEnrollmentForm />} />
              <Route path="print/enrollment/:id" element={<PrintEnrollment />} />
              <Route path="enrollment" element={<Enrollment />} />
            </Route>
            <Route element={<ProtectedRoute allowedRoles={["student", "parent", "registrar", "admin", "finance", "superadmin"]} user={user}><Outlet /></ProtectedRoute>}>
              <Route path="dashboard" element={<SISDashboard />} />
              <Route path="print/list" element={<PrintList />} />
              <Route path="school-year" element={<SchoolYearManager />} />
              <Route path="subjects" element={<Subjects />} />
              <Route path="cor/:id" element={<StudentCOR />} />
              <Route path="grades" element={<div>Grades Page (Coming Soon)</div>} />
              <Route path="finance" element={<SISFinance />} />
              <Route path="attendance" element={<Attendance />} />
            </Route>
          </Route>

          {/* ✅ Fallback Redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <UserProvider>
      <AppContent />
    </UserProvider>
  );
}

export default App;
